import React from 'react';
import { InvitationDto } from '../types/settings';
import { updateInvitationStatus } from '../api/settingsApi';
import { Check, X, Mail, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '../contexts/ToastContext';
import './ReceivedInvitationsWidget.css';

interface ReceivedInvitationsWidgetProps {
  invitations: InvitationDto[];
}

export const ReceivedInvitationsWidget: React.FC<ReceivedInvitationsWidgetProps> = ({
  invitations
}) => {
  const { showError } = useToast();
  
  // Only show pending invitations
  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');

  // Don't render if no pending invitations
  if (pendingInvitations.length === 0) {
    return null;
  }

  const handleInvitationResponse = async (invitationId: number, status: 'accepted' | 'declined') => {
    try {
      await updateInvitationStatus(invitationId, status);
      // Success message and cache invalidation handled by SignalR
    } catch (error) {
      showError(`Failed to ${status === 'accepted' ? 'accept' : 'decline'} invitation. Please try again.`);
    }
  };

  return (
    <div className="received-invitations-widget">
      <div className="widget-header">
        <Mail size={16} />
        <span className="widget-title">
          {pendingInvitations.length === 1 ? 'New Invitation' : `${pendingInvitations.length} New Invitations`}
        </span>
      </div>
      
      <div className="widget-content">
        {pendingInvitations.map((invitation) => (
          <div key={invitation.id} className="invitation-item">
            <div className="invitation-info">
              <div className="invitation-from">
                <strong>{invitation.inviterName}</strong>
                <span className="invitation-email">({invitation.inviterEmail})</span>
                <span className="invitation-message">invited you to collaborate</span>
              </div>
              <div className="invitation-meta">
                <Clock size={12} />
                <span>{formatDistanceToNow(new Date(invitation.createdAt))} ago</span>
              </div>
            </div>
            
            <div className="invitation-actions">
              <button
                onClick={() => handleInvitationResponse(invitation.id, 'accepted')}
                className="invitation-btn accept"
                title="Accept invitation"
                aria-label="Accept invitation"
              >
                <Check size={14} />
                Accept
              </button>
              <button
                onClick={() => handleInvitationResponse(invitation.id, 'declined')}
                className="invitation-btn decline"
                title="Decline invitation"
                aria-label="Decline invitation"
              >
                <X size={14} />
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
